import React, {
	Fragment,
	useEffect,
	useRef,
	useState,
	useContext,
} from 'react';
import { ApSimpleDropdown } from '@alixpartners/ui-components';
import {
	exportSpendByVendorChart,
	exportSpendByVendors,
	getCompaniesComparisonInfo,
	getCompaniesSpendComparison,
	getShareOfTotalSpend,
	changeCategorisation,
} from 'utils/apiRequests';
import {
	CompaniesTopVendors,
	HeaderLabel,
	HeaderTop,
	Radio,
	SectionWrapper,
	ShowMore,
	SortSelectorContainer,
	SortText,
} from '../styles';
import { SpendByVendorChart } from './SpendByVendorChart';
import { SpendByVendorTable } from './SpendByVendorTable';
import { usePrevious } from '../utils';
import { CHART_COLORS } from 'styles/colors';
import { isEqual } from 'lodash';
import { handleExport } from './utils';
import { useCallback } from 'react';
import { SPEND_TYPE } from 'Data/constants';
import CategorisationDialog from 'Explore/CategorisationDialog';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import { PmiContext } from '../context';
import { TAIL_SPEND } from 'utils/detailsChartDataConverter';
import remove from 'lodash/remove';

export const NONE_ORDER = 'None';

const createChartSeries = shareOfTotalSpendResponse => {
	return shareOfTotalSpendResponse.map((companyData, i) => ({
		color: CHART_COLORS[i],
		name: companyData.companyName,
		data: companyData.data.map(
			({ vendorAmount, spend, percentageOfTotal }) => ({
				x: vendorAmount,
				y: percentageOfTotal,
				spend,
			})
		),
	}));
};

const ShareOfTotalSpendContainer = ({
	currencySymbol,
	queryParams,
	spendType,
	onDownloadStart,
	onDownloadSuccess,
	onDownloadError,
	showExplorePage,
	pmiBreadcrumbs,
	companies,
}) => {
	const chartRef = useRef();
	const [sortOrderCompany, setSortOrderCompany] = useState(NONE_ORDER);
	const pmiContext = useContext(PmiContext);
	const [chartSeries, setChartSeries] = useState([]);
	const [categorisationModalData, setCategorisationModalData] = useState(null);
	const [changeStatusModalData, setChangeStatusModalData] = useState(null);
	const [moveToBinModalData, setMoveToBinModalData] = useState(null);
	const [showMoreAmount, setShowMoreAmount] = useState(10);
	const [companiesData, setCompaniesData] = useState([]);
	const [comparisonInfo, setComparisonInfo] = useState([]);
	const { filterValues } = queryParams;
	const sortByInRequest =
		sortOrderCompany !== NONE_ORDER && filterValues.includes(sortOrderCompany)
			? sortOrderCompany
			: undefined;
	const params = {
		...queryParams,
		take: showMoreAmount,
		sortBy: sortByInRequest,
	};
	const prevParams = usePrevious(params);
	useEffect(
		() => {
			const { filterValues } = queryParams;
			if (
				sortOrderCompany !== NONE_ORDER &&
				!filterValues.includes(sortOrderCompany)
			) {
				setSortOrderCompany(NONE_ORDER);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryParams]
	);
	let isSkipInitialSetData = false;

	useEffect(() => {
		getShareOfTotalSpend(params).then(result => {
			// TODO do not render components in Pmi.js until state is normalized
			if (!isSkipInitialSetData) {
				setChartSeries(createChartSeries(result));
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(
		() => {
			if (prevParams && params.take !== prevParams.take) {
				getCompaniesSpendComparison({
					...params,
					skip: companiesData[0].spends.length,
					take: 10,
				}).then(result => {
					let companies = companiesData;
					setCompaniesData(
						companies.map(company => {
							const foundCompany = result.find(
								companyResults => companyResults.company === company.company
							);
							const newSpends = foundCompany ? foundCompany.spends : [];
							return {
								...company,
								spends: [...company.spends, ...newSpends],
							};
						})
					);
				});
			} else if (!isEqual(prevParams, params)) {
				// eslint-disable-next-line react-hooks/exhaustive-deps
				isSkipInitialSetData = true;
				const companyParams = {
					...params,
					skip: 0,
				};

				getShareOfTotalSpend(queryParams).then(result => {
					setChartSeries(createChartSeries(result));
				});
				getCompaniesComparisonInfo(companyParams).then(result =>
					setComparisonInfo(result)
				);
				getCompaniesSpendComparison(companyParams).then(result =>
					setCompaniesData(result)
				);
			}
		},
		[params, prevParams]
	);

	const getChartInstance = () => {
		return chartRef && chartRef.current && chartRef.current.chart;
	};

	const tableData = companiesData.map(companyData => {
		const companyInfo =
			comparisonInfo.find(info => info.company === companyData.company) || {};
		return {
			...companyData,
			...companyInfo,
		};
	});

	const isSomeVendorsNotLoaded = tableData.some(
		({ spends, vendorsCount }) => spends.length !== vendorsCount
	);
	const handleChartDataExport = useCallback(
		() => {
			onDownloadStart();
			exportSpendByVendorChart(params)
				.catch(onDownloadError)
				.finally(onDownloadSuccess);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[params]
	);

	const handleTableDataExport = useCallback(
		() => {
			onDownloadStart();
			exportSpendByVendors(params)
				.catch(onDownloadError)
				.finally(onDownloadSuccess);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[params]
	);

	const getVendorTransactionsParams = () => {
		if (!categorisationModalData) {
			return {};
		}

		return {
			filters: ['harmonizedSupplier'],
			filterValues: [categorisationModalData.vendorName],
		};
	};

	const getContextParams = () => {
		if (!categorisationModalData) {
			return {};
		}

		const companyIndexes = [];
		const filtersWithoutCompanies = queryParams.filters.filter(
			(item, index) => {
				if (item === 'company') {
					companyIndexes.push(index);

					return false;
				}

				return true;
			}
		);
		const filterValuesWithoutCompanies = [...queryParams.filterValues];

		remove(filterValuesWithoutCompanies, (_item, index) =>
			companyIndexes.includes(index)
		);

		return {
			dateFrom: queryParams.dateFrom,
			dateTo: queryParams.dateTo,
			hierarchy: queryParams.hierarchy,
			filters: [...filtersWithoutCompanies, 'harmonizedSupplier', 'company'],
			filterValues: [
				...filterValuesWithoutCompanies,
				categorisationModalData.vendorName,
				categorisationModalData.company,
			],
		};
	};

	const handleChangeCategorisation = ({
		taxonomy,
		isDirect,
		isAllTransactions,
	}) => {
		const params = isAllTransactions
			? getVendorTransactionsParams()
			: getContextParams();
		const spendType = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
		const data = !isDirect
			? {
					l4Code: taxonomy.aP_L4Code,
			  }
			: {
					l1Category: taxonomy.project_L1Category,
					l2Category: taxonomy.project_L2Category,
					l3Category: taxonomy.project_L3Category,
					l4Category: taxonomy.project_L4Category,
			  };

		return changeCategorisation(spendType, params, data).then(refreshContent);
	};

	const refreshContent = useCallback(
		() => {
			const { refreshPmiContent } = pmiContext;

			if (refreshPmiContent) {
				refreshPmiContent();
			}
		},
		[pmiContext]
	);

	const handleViewTransactionsClick = (row, { company }) => {
		const options = {};
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);
		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;
		if (spendType) {
			options.spendType = spendType.toUpperCase();
		}
		if (exploreHierarchy && exploreHierarchy.length > 0) {
			options.hierarchy = exploreHierarchy;
		}
		// filter by vendor name
		options.vendorName = row.vendorName;
		// filter by company name
		options.companies = [company];
		showExplorePage(options, row.vendorName);
	};

	const showCategorisationModal = data => {
		setCategorisationModalData(data);
	};

	const hideCategorisationModal = () => {
		setCategorisationModalData(null);
	};

	const handleChangeStatusClick = row => {
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);

		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		const data = {
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(exploreHierarchy.length > 0 ? { hierarchy: exploreHierarchy } : {}),
			extraFilters: {
				company: [row.company],
			},
		};

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		setChangeStatusModalData(data);
	};

	const handleMoveToBinClick = row => {
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);

		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;

		const data = {
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(exploreHierarchy.length > 0 ? { hierarchy: exploreHierarchy } : {}),
			extraFilters: {
				company: [row.company],
			},
		};

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		setMoveToBinModalData(data);
	};

	return (
		<SectionWrapper>
			<HeaderTop>
				<HeaderLabel>Share of total spend by vendor</HeaderLabel>
				<ApSimpleDropdown
					iconName="outline_save_alt"
					onChange={s => {
						if (s.value === 'png') {
							handleExport(getChartInstance(), 'SXR-Spend_by_vendor');
						} else if (s.value === 'chart_xlsx') {
							handleChartDataExport();
						} else if (s.value === 'table_xlsx') {
							handleTableDataExport();
						}
					}}
					options={[
						{
							label: 'Export chart as image',
							value: 'png',
						},
						{
							label: 'Export chart as data',
							value: 'chart_xlsx',
						},
						{
							label: 'Export table as data',
							value: 'table_xlsx',
						},
					]}
					label="Export"
				/>
			</HeaderTop>
			<SpendByVendorChart
				chartRef={chartRef}
				currencySymbol={currencySymbol}
				series={chartSeries}
			/>
			<SortSelectorContainer>
				<SortText>Sort all tables by order of:</SortText>
				<Radio
					value={NONE_ORDER}
					checked={sortOrderCompany === NONE_ORDER}
					onChange={e => {
						setSortOrderCompany(e.target.value);
					}}
				>
					{NONE_ORDER}
				</Radio>
				<Fragment>
					{tableData.map(({ company }) => (
						<Radio
							key={company}
							value={company}
							checked={sortOrderCompany === company}
							onChange={e => {
								setSortOrderCompany(e.target.value);
							}}
						>
							{company}
						</Radio>
					))}
				</Fragment>
			</SortSelectorContainer>
			<CompaniesTopVendors>
				{tableData.map(data => (
					<SpendByVendorTable
						key={data.company}
						companyData={data}
						sortOrderCompany={sortOrderCompany}
						currencySymbol={currencySymbol}
						showCategorisationModal={showCategorisationModal}
						onViewTransactionsClick={handleViewTransactionsClick}
						showChangeStatusModal={data => handleChangeStatusClick(data)}
						showMoveToBinModal={data => handleMoveToBinClick(data)}
					/>
				))}
			</CompaniesTopVendors>
			{isSomeVendorsNotLoaded && (
				<ShowMore
					onClick={() => {
						setShowMoreAmount(showMoreAmount + 10);
					}}
				>
					show 10 more items
				</ShowMore>
			)}
			{categorisationModalData && (
				<CategorisationDialog
					name={categorisationModalData.vendorName}
					onClose={hideCategorisationModal}
					onSave={handleChangeCategorisation}
					contextParams={getContextParams()}
					allTransactionsParams={getVendorTransactionsParams()}
				/>
			)}
			{changeStatusModalData && (
				<ChangeTypeModal
					isOpen={changeStatusModalData}
					isHarmonized
					onClose={() => {
						setChangeStatusModalData(null);
					}}
					onSubmit={refreshContent}
					spendType={spendType}
					{...changeStatusModalData}
					isPmi
				/>
			)}
			{moveToBinModalData && (
				<MoveToBinModal
					isOpen={moveToBinModalData}
					isHarmonized
					onClose={() => {
						setMoveToBinModalData(null);
					}}
					onSubmit={refreshContent}
					spendType={spendType}
					{...moveToBinModalData}
					isPmi
				/>
			)}
		</SectionWrapper>
	);
};

export default ShareOfTotalSpendContainer;
